// RiskProfileSummary (TASK-579 Phase 6 / B5) — compact 3-dimension reconciliation:
// the determined category + risk CAPACITY (ability) vs TOLERANCE (willingness) vs
// REQUIRED (goal-driven return need), with a goal-gap flag when the return target needs
// more risk than the profile supports. FCA "narrative not number": labelled dimensions,
// not a chart. Self-fetches by clientId via riskProfileService (no new endpoint).
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import riskProfileService from '../../../services/riskProfileService';
import { determineRiskCapacity } from '../../risk-profile/schemas/riskProfileSchema';
import { WidgetContainer } from '../WidgetContainer';

// Severity rank of a determined/required category (matches shared/risk RiskProfile order).
// Exported for the drift test that pins this to the zod determinedProfile enum.
export const RANK: Record<string, number> = {
  veryConservative: 0, conservative: 1, balanced: 2, growthOriented: 3, aggressive: 4,
};
// Minimum profile a given return expectation needs (matches shared/risk _REQUIRED_MIN).
export const REQUIRED_MIN: Record<string, string> = {
  preservation: 'veryConservative', conservative: 'conservative', moderate: 'balanced', aggressive: 'growthOriented',
};

const LABEL: Record<string, string> = {
  veryConservative: 'Very conservative', conservative: 'Conservative', balanced: 'Balanced',
  growthOriented: 'Growth-oriented', aggressive: 'Aggressive',
};

export function RiskProfileSummary({ clientId }: { clientId: string }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['risk-profile-summary', clientId],
    queryFn: () => riskProfileService.get(clientId),
    enabled: !!clientId,
  });

  // TASK-931: which VERSION this is. The profile is an append-only record now — completing it
  // again writes a new version rather than overwriting the last — so "Balanced, completed"
  // without a version says nothing about WHICH completed profile the reader is looking at,
  // and that is exactly what a suitability verdict is a statement about.
  //
  // Its own query: a failure here must degrade to "no version shown", never take out the
  // profile summary itself.
  const { data: versionData } = useQuery({
    queryKey: ['risk-profile-versions', clientId],
    queryFn: () => riskProfileService.listVersions(clientId),
    enabled: !!clientId,
  });
  const currentVersion = versionData?.current ?? null;

  const response = data as
    | {
        risk_profile?: Record<string, Record<string, unknown>>;
        status?: string;
        completion?: { percent: number; complete_sections: number; total_sections: number };
      }
    | undefined;
  const rp = response?.risk_profile ?? {};
  // TASK-658: the canonical server status/completion — a determined label must
  // never imply completeness.
  const status = response?.status ?? 'not_started';
  const completion = response?.completion;
  const s3 = rp.section3 ?? {};
  const s4 = rp.section4 ?? {};
  const s5 = rp.section5 ?? {};
  const determined = (rp.section6?.determinedProfile as string) || null;

  // Capacity via the single FE chokepoint (cast: section3 carries the FinancialSituation fields).
  let capacity: string | null = null;
  try {
    capacity = s3 && Object.keys(s3).length ? determineRiskCapacity(s3 as never) : null;
  } catch {
    capacity = null;
  }
  const tolerance = (s5.riskAttitude as string) || null;
  const expectedReturn = (s4.expectedReturn as string) || null;
  const requiredMin = expectedReturn ? REQUIRED_MIN[expectedReturn] : null;
  const goalGap = !!(requiredMin && determined && RANK[requiredMin] > RANK[determined]);

  return (
    <WidgetContainer source="Risk · Profile summary">
      {isLoading && <div className="h-24 w-full animate-pulse rounded bg-accent" />}
      {isError && <div className="text-[13px] text-muted-foreground">Could not load risk profile.</div>}
      {data && (
        <div>
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[15px] font-semibold text-foreground">Risk profile</span>
            <span className="rounded-full bg-accent px-2 py-0.5 text-[12px] font-medium text-foreground">
              {determined ? LABEL[determined] ?? determined : 'Not determined'}
              {determined && status !== 'completed' && (
                <span className="ml-1 font-normal text-muted-foreground">· provisional</span>
              )}
            </span>
          </div>

          {/* TASK-931: "v2, completed 4 Sep 2026". Absent until the profile has been completed
              once — no version is a different fact from v0 and must not render as one. */}
          {currentVersion && (
            <p
              data-testid="risk-profile-version"
              className="mb-3 text-[12px] text-muted-foreground"
            >
              v{currentVersion.version}
              {currentVersion.status === 'cleared'
                ? ' · withdrawn'
                : currentVersion.completed_at
                  ? `, completed ${new Date(currentVersion.completed_at).toLocaleDateString()}`
                  : ''}
            </p>
          )}

          {/* TASK-658: an incomplete profile says so — "Balanced" over blank
              capacity/tolerance must never read as a usable determination. */}
          {status !== 'completed' && (
            <div
              data-testid="risk-provisional-banner"
              className="mb-3 rounded border border-status-attention/30 bg-status-attention/10 p-2 text-[12px] text-status-attention-fg"
            >
              Risk profile {completion ? `${completion.percent}% complete` : 'incomplete'}
              {completion ? ` (${completion.complete_sections}/${completion.total_sections} sections)` : ''} —
              not usable for suitability until every required section, declaration, and
              signature is complete.
            </div>
          )}

          <dl className="space-y-1.5 text-[13px]">
            <Row
              label="Capacity (ability)"
              value={capacity ? capacity[0].toUpperCase() + capacity.slice(1) : 'Not recorded'}
              unavailable={!capacity}
            />
            <Row
              label="Tolerance (willingness)"
              value={tolerance ? LABEL[tolerance] ?? tolerance : 'Not recorded'}
              unavailable={!tolerance}
            />
            <Row
              label="Required (goal)"
              value={requiredMin ? LABEL[requiredMin] ?? requiredMin : 'Not recorded'}
              unavailable={!requiredMin}
            />
          </dl>

          {goalGap && (
            <div className="mt-3 flex items-start gap-1.5 rounded bg-amber-50 p-2 text-[12px] text-amber-800">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Goal gap — the return target needs a {LABEL[requiredMin!] ?? requiredMin} profile, above the
                determined {LABEL[determined!] ?? determined}. The goal may be unattainable within this risk profile.
              </span>
            </div>
          )}

          <p className="mt-3 border-t border-border pt-2 text-[11px] text-muted-foreground/70">
            Capacity binds: the most restrictive realistic factor carries the most weight.
          </p>
        </div>
      )}
    </WidgetContainer>
  );
}

function Row({ label, value, unavailable }: { label: string; value: string; unavailable?: boolean }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={unavailable ? 'font-normal italic text-status-unavailable-fg' : 'font-medium text-foreground'}>
        {value}
      </dd>
    </div>
  );
}
